/**
 * English strings of Cài đặt › Module › Tiện ích (add-ons, ADR-025 §4.6): the add-ons of the build,
 * their lifecycle, the events they have not handled and their own pages. The Vietnamese file must
 * have exactly the same keys. Every code the server may show has a name here
 * (`tests/module-addons.test.mjs` reads the server's lists).
 */
export const addonModules = {
  list: {
    workerMissing: 'The add-ons cannot be reached from the Control Center right now: their pages and actions do not open. Installing, switching on and off and uninstalling still work.',
    none: 'No add-on in this build',
    noneHint: 'Add-ons come with the platform’s releases. When a release brings one, it shows here, ready to be installed.',
    notInBuildTitle: 'Add-ons no longer in this build',
    notInBuildHint: 'Releases that dropped them leave their records: data kept locked, or an encrypted copy kept until the date shown.',
    columns: { addon: 'Add-on', state: 'Status', data: 'Its data', updated: 'Last change' },
    back: 'All add-ons',
    loadFailed: 'The add-ons could not be loaded',
  },
  state: {
    NOT_INSTALLED: 'Not installed',
    INSTALL_PENDING: 'Installing',
    INSTALLED: 'Installed, off',
    ACTIVE: 'Running',
    INACTIVE: 'Switched off',
    DEGRADED: 'Stopped after failures',
    UNINSTALL_PENDING: 'Uninstalling',
    UNINSTALLED: 'Uninstalled',
  },
  stateHint: {
    NOT_INSTALLED: 'Not installed on this server.',
    INSTALL_PENDING: 'Waiting for the server to prepare the add-on’s own data store. This happens with the next system update.',
    INSTALLED: 'Its data store is ready; nothing of it runs until it is switched on.',
    ACTIVE: 'It receives events, runs its scheduled tasks and shows its pages.',
    INACTIVE: 'Nothing of it runs; its data and settings are kept.',
    DEGRADED: 'It failed too often in a row and was stopped by itself. Its events wait for it.',
    UNINSTALL_PENDING: 'Waiting for the server to remove it as chosen. This happens with the next system update.',
    UNINSTALLED: 'Removed from this server.',
  },
  request: {
    install: 'Install',
    activate: 'Switch on',
    deactivate: 'Switch off',
    uninstall: 'Uninstall',
  },
  retention: {
    KEEP_DATA: 'Keep the data',
    EXPORT_THEN_DROP: 'Keep an encrypted copy, then delete',
    DROP: 'Delete now',
  },
  retentionHint: {
    KEEP_DATA: 'The data stays, locked: nothing reads it until the add-on is installed again, which uses it.',
    EXPORT_THEN_DROP: 'An encrypted copy of everything it stored is kept for the days chosen, then deleted. The data itself is deleted now.',
    DROP: 'Everything it stored is deleted. This cannot be undone.',
  },
  card: {
    version: 'Add-on version {{version}}',
    vendor: 'Provider',
    capabilities: 'What it can do',
    installed: 'Installed version',
    notInstalled: 'Not installed',
    settings: 'Settings',
    settingsNone: 'None saved: it uses its default values.',
    settingsSaved: 'Saved settings in use.',
    settingsDraft: 'Saved, not applied yet.',
    settingsOpen: 'Open settings',
    settingsCreate: 'Create settings',
    noSettings: 'It has no setting.',
    waiting: 'Events not handled',
    waitingNone: 'None.',
    waitingCount: '{{parked}} held back, {{retrying}} being retried',
    open: 'Open',
    notProductionReady: 'For development and tests only: it can never be installed on the production server.',
    degraded: 'Stopped after repeated failures: “{{reason}}”. Switch it on again once the cause is found; its events waited for it.',
    degradedNoReason: 'Stopped after repeated failures. Switch it on again once the cause is found; its events waited for it.',
    exportUntil: 'An encrypted copy of its data is kept until {{date}}.',
    dataKept: 'Its data is kept, locked.',
    dataDropped: 'Its data was deleted.',
    noRequest: 'Nothing can be asked of it while it is in this state.',
  },
  approval: {
    pending: 'Waiting for a second person to approve: {{change}}.',
    pendingSelf: 'You asked for it: another person approves it in Phê duyệt.',
    approved: 'Approved: {{change}}. It applies once someone confirms it here.',
    apply: 'Apply the approved change',
    open: 'Open the request',
    change: '{{request}}',
    changeWithData: '{{request}} ({{retention}})',
    changeWithDays: '{{request}} ({{retention}}, {{count}} days)',
  },
  dialog: {
    title: '{{request}}: {{name}}',
    install: 'The server prepares the add-on’s own data store with the next system update. It stays off until someone switches it on.',
    activate: 'The add-on starts receiving events, running its scheduled tasks and showing its pages.',
    activateFirst: 'The first time it is switched on, it may read the platform’s data and act as add-ons are allowed to.',
    deactivate: 'Everything the add-on does stops at once. Its data and settings stay; events that arrive meanwhile wait for it.',
    uninstall: 'Choose what happens to the data the add-on stored. Its settings are kept.',
    retentionDays: 'Keep the copy for (days)',
    retentionDaysHint: 'From 1 to 3650 days.',
    fourEyes: 'A second person approves this change before it applies.',
    fourEyesDrop: 'Deleting the data always needs a second person’s approval.',
    noApproval: 'This change needs no second person’s approval.',
    applyApproved: 'Another person approved this change. Applying it now does it.',
    submit: 'Send',
    submitForApproval: 'Send for approval',
    cancel: 'Cancel',
    close: 'Close',
    requested: 'Sent for approval. Once another person approves it (Phê duyệt), come back here to apply it.',
    applied: 'Done: the add-on is now “{{state}}”.',
    storageStep: 'The server finishes it with the next system update.',
  },
  settingsDialog: {
    title: 'Create the settings of {{name}}',
    description: 'The settings start from the add-on’s default values. Change them on the settings page, then apply them there.',
    submit: 'Create',
    created: 'Created. Open the settings page to change them and apply them.',
  },
  events: {
    title: 'Events not handled yet',
    hint: 'Platform events the add-on failed to handle. Those being retried are tried again by themselves; held-back ones wait until someone sends them again.',
    none: 'No event is waiting.',
    filterAll: 'All',
    columns: { event: 'Event', occurred: 'Happened', status: 'Status', attempts: 'Tries', next: 'Next try', reason: 'What the add-on said' },
    status: { RETRYING: 'Being retried', PARKED: 'Held back' },
    replay: 'Send again',
    replayTitle: 'Send the event to the add-on again',
    replayDescription: 'The add-on receives this event once more, as if it had just happened. It is tried again by itself if it fails.',
    replayDone: 'Sent again.',
    otherEvent: 'Event about {{what}}',
    about: {
      domain: 'a domain',
      payment: 'a payment',
      order: 'an order',
      customer: 'a customer',
      invoice: 'an invoice',
      subject: 'a holder',
      dossier: 'a dossier',
      other: 'the platform',
    },
    loadFailed: 'The waiting events could not be loaded',
  },
  pages: {
    title: 'Pages of the add-on',
    notRunning: 'The add-on is not running: switch it on to open its pages.',
    none: 'This add-on has no page.',
    loadFailed: 'The add-on’s pages could not be loaded',
    section: 'Shown under {{section}}',
    readOnly: 'You can look but not act: acting on add-on pages needs the right to use add-ons.',
  },
  section: {
    domains: 'Domains',
    customers: 'Customers',
    sales: 'Sales',
    products: 'Products',
    compliance: 'Compliance',
    ops: 'Operations',
    settings: 'Settings',
  },
  block: {
    loadFailed: 'This part could not be loaded',
    filter: 'Filter',
    clear: 'Clear',
    more: 'Load more',
    empty: 'No row.',
    submit: 'Send',
    confirmTitle: 'Confirm',
    confirm: 'Go ahead',
    cancel: 'Cancel',
    dong: '{{amount}} ₫',
    yes: 'Yes',
    no: 'No',
  },
};
